import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import AppointmentFormDialog from "components/Modals/AppointmentFormDialog";
import { API_URL } from "const/constants";
import DisplayDate, { DisplayDateTime } from "components/DisplayDate";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppointmentDeleteDialog from "views/admin/Appointments/AppointmentDeleteDialog";
import { invalidateEmailLogQueries } from "views/admin/Appointments/appointmentNotifyUtils";
import { formatAppointmentTimeDisplay } from "views/admin/Appointments/appointmentUtils";

const statusColor = {
  SCHEDULED: "primary",
  COMPLETED: "success",
  CANCELLED: "error",
  RESCHEDULED: "warning",
};

const ClientAppointments = () => {
  const { id: clientId } = useParams();
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAppt, setEditingAppt] = useState(null);
  const [reminderApptId, setReminderApptId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data: appointments = [], isFetching } = useQuery({
    queryKey: ["client-appointments", clientId],
    queryFn: () =>
      axios.get(`${API_URL}/appointments/client/${clientId}`).then((r) => r.data?.data ?? []),
    enabled: !!clientId,
    refetchOnWindowFocus: false,
  });

  const refetch = () => queryClient.invalidateQueries(["client-appointments", clientId]);

  const { mutate: remove, isLoading: isDeleting } = useMutation({
    mutationFn: ({ id, sendEmail }) =>
      axios.delete(`${API_URL}/appointments/${id}`, { params: { sendEmail } }),
    onSuccess: () => {
      toast.success("Appointment deleted");
      setDeleteTarget(null);
      refetch();
      queryClient.invalidateQueries(["client-summary", clientId]);
      invalidateEmailLogQueries(queryClient);
    },
    onError: () => toast.error("Failed to delete appointment"),
  });

  const { mutate: sendReminder, isLoading: isSendingReminder } = useMutation({
    mutationFn: (apptId) => axios.post(`${API_URL}/appointments/${apptId}/send-reminder`),
    onSuccess: () => {
      toast.success("Reminder sent");
      setReminderApptId(null);
      refetch();
      invalidateEmailLogQueries(queryClient);
    },
    onError: () => toast.error("Failed to send reminder"),
  });

  const handleOpen = useCallback((appt = null) => {
    setEditingAppt(appt);
    setDialogOpen(true);
  }, []);

  const handleClose = () => {
    setDialogOpen(false);
    setEditingAppt(null);
  };

  const columns = useMemo(
    () => [
      { accessorKey: "title", header: "Title", size: 200 },
      {
        accessorKey: "createdDate",
        header: "Scheduled On",
        size: 130,
        Cell: ({ row }) => <DisplayDate value={row.original.createdDate} />,
      },
      { accessorKey: "appointmentDate", header: "Appt Date", size: 110 },
      {
        accessorKey: "appointmentTime",
        header: "Time",
        size: 90,
        Cell: ({ row }) => formatAppointmentTimeDisplay(row.original) || "-",
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ row }) => (
          <Chip
            label={row.original.status}
            size="small"
            color={statusColor[row.original.status] || "default"}
            variant="outlined"
          />
        ),
      },
      {
        accessorKey: "withUserName",
        header: "With",
        size: 140,
        Cell: ({ row }) => row.original.withUserName || "-",
      },
      {
        accessorKey: "reminderSentAt",
        header: "Reminder Sent",
        size: 150,
        Cell: ({ row }) => <DisplayDateTime value={row.original.reminderSentAt} />,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => {
          const inactive = ["CANCELLED", "COMPLETED"].includes(row.original.status);
          return (
            <Box className="flex gap-1">
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => handleOpen(row.original)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={inactive ? `Not available for ${row.original.status}` : "Send Reminder"}
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={inactive}
                    onClick={() => setReminderApptId(row.original.id)}
                  >
                    <SendIcon fontSize="small" color={inactive ? "disabled" : "primary"} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => setDeleteTarget(row.original)}>
                  <DeleteIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [handleOpen]
  );

  const table = useMaterialReactTable({
    columns,
    data: appointments,
    initialState: { density: "compact", showGlobalFilter: true },
    state: { isLoading: isFetching },
    enableExpanding: true,
    renderDetailPanel: ({ row }) => {
      const a = row.original;
      return (
        <Box sx={{ p: 2, backgroundColor: "background.default" }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={3} flexWrap="wrap">
            {Array.isArray(a.participants) && a.participants.length > 1 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  CLIENTS
                </Typography>
                <Typography variant="body2">
                  {a.participants
                    .map((p) => p.clientName)
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
              </Box>
            )}
            {a.location && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  LOCATION
                </Typography>
                <Typography variant="body2">{a.location}</Typography>
              </Box>
            )}
            {a.meetLink && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  MEET LINK
                </Typography>
                <Typography variant="body2">
                  <a
                    href={a.meetLink}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary underline"
                  >
                    {a.meetLink}
                  </a>
                </Typography>
              </Box>
            )}
            {a.withUserEmail && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  WITH EMAIL
                </Typography>
                <Typography variant="body2">{a.withUserEmail}</Typography>
              </Box>
            )}
            {a.ccEmails && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  CC
                </Typography>
                <Typography variant="body2">{a.ccEmails}</Typography>
              </Box>
            )}
          </Stack>
          {a.description && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                DESCRIPTION
              </Typography>
              <Typography variant="body2">{a.description}</Typography>
            </>
          )}
          {a.notes && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                NOTES
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {a.notes}
              </Typography>
            </>
          )}
        </Box>
      );
    },
    renderTopToolbarCustomActions: () => (
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => handleOpen()}
        sx={{ ml: 1 }}
      >
        Add Appointment
      </Button>
    ),
    muiTableContainerProps: { sx: { maxHeight: "60vh" } },
    enableStickyHeader: true,
    paginationDisplayMode: "pages",
    muiPaginationProps: { rowsPerPageOptions: [20, 50, 100] },
  });

  return (
    <>
      <MaterialReactTable table={table} />

      <AppointmentFormDialog
        open={dialogOpen}
        onClose={handleClose}
        appointment={editingAppt}
        clientId={clientId ? Number(clientId) : null}
      />

      <AppointmentDeleteDialog
        open={!!deleteTarget}
        appointment={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        isDeleting={isDeleting}
        onConfirm={({ sendEmail }) => remove({ id: deleteTarget.id, sendEmail })}
      />

      {/* Send Reminder Confirm Dialog */}
      <Dialog
        open={!!reminderApptId}
        onClose={() => setReminderApptId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Send Reminder</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Send the appointment reminder email to all clients on this appointment using the default
            Appointment Reminder template?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderApptId(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={isSendingReminder}
            onClick={() => sendReminder(reminderApptId)}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientAppointments;
